// Partner visits API (2026-10-06): see docs/superpowers/specs/2026-10-06-partner-visits-api-design.md

import { h, render, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { IntlProvider, Text, Localizer, withText } from 'preact-i18n';
import { route } from 'preact-router';
import Markdown from 'preact-markdown';

import localUtil from '../../lib/util';
import fields from '../../lib/fields';
import { printViaIframe } from '../../lib/printHelper';
import { sharedSalesEnabled } from '../../lib/crossCustomerSales';
import { hasClientAdvice } from '../../lib/journalAdvice';

import FormFields from '../../components/form/fields';
import ViewFields from '../../components/form/viewFields';
import Input from '../../components/form/input';
import DyrejournalAnimalListMini from '../../components/dyrejournal/listAnimalMini';
import DyrejournalPrescriptionListMini from '../../components/dyrejournal/listPrescriptionMini';
import DyrejournalLabResultsListMini from '../../components/dyrejournal/listLabResultsMini';
import PartnerLaunchButtons from './partnerLaunchButtons';
import PartnerSuggestionsBanner from './partnerSuggestionsBanner';
import { pendingSectionsFromJournal } from '../../lib/partnerSuggestions';
import DentalChartSection from '../../components/dyrejournal/dentalChartSection';
import Versions from '../../components/versions';
import DyrejournalJournalPrint from '../../components/dyrejournal/journalPrint';
import DyrejournalLinkJournalToSale from '../../components/dyrejournal/linkJournalToSale';
import JournalFeedback from '../../components/dyrejournal/journalFeedback';

import ImageScroller from '../../components/imagescroller';
import LineGraphMini from '../../components/lineGraph/mini';

import definitionNo from '../../languages/no.json';
import definitionEn from '../../languages/en.json';
import { appConfirm } from '../../lib/dialogs';
import { beginDialogAction } from '../../lib/dialogAction';
import { animalAge } from '../../lib/animalAge';

const countryMap = {
    no: definitionNo,
    en: definitionEn,
    default: definitionEn,
};

function sortByDateAsc(a, b) {
    const dateA = new Date(a.date);
    const dateB = new Date(b.date);
    return dateA - dateB;
}

@withText(props => ({
    switchClinicConfirm: <Text id='dyrejournal.journal-switch-clinic-confirm'>This sale is registered on another clinic. Switch to that clinic to open the sale?</Text>,
}))
@observer
class ViewJournal extends Component {
	constructor(props) {
        super(props);
        this.state = {
            sections: fields.get('journal', 'Journal basics'),
            sectionsMedical: fields.get('journal', 'Journal medical'),
            sectionsClinical: fields.get('journal', 'Journal clinical'),
            sectionsPassport: fields.get('journal', 'Journal passport'),
            sectionsExtra: fields.get('journal', 'Journal extra'),
            sectionsMedia: fields.get('journal', 'Journal media'),
        };
        this.searchTimer = null;
    }

    toggleWeightView = async () => {
        const { userStore } = this.props.stores;
        const { user } = userStore;
        const { settings = {} } = user;
        const current = settings.weightViewMode || 'graph';
        const newValue = current === 'graph' ? 'table' : 'graph';
        await userStore.updateSetting(
            { 'settings.weightViewMode': newValue },
            { weightViewMode: newValue },
        );
    }

    onClickGotoSale = async e => {
        e.stopPropagation();
        e.preventDefault();
        const dialogAction = beginDialogAction(this, 'onClickGotoSale', () => [
            this.props.stores.journalStore.journal?.id,
        ]);
        if (!dialogAction) return false;
        try {
            // Open the sale linked to this journal on the sales page, ready for payment.


            const { appState, userStore, saleStore, journalStore, visitorStore, animalStore, calendarStore } = this.props.stores;
            const { journal = {} } = journalStore;
            const saleId = parseInt(journal.sale, 10);
            const journalCustomerId = parseInt(journal.customer, 10);
            if (!saleId) {
                return;
            }
            const { switchClinicConfirm } = this.props;
            const activeCustomerId = userStore.customer?.id;

            // Sales are scoped to a single clinic, but the journal view spans every clinic
            // the user has access to. If this sale belongs to another clinic than the
            // active one, confirm and switch clinic first so the sale — and any payment
            // registered on it — lands on the correct clinic.
            if (journalCustomerId && activeCustomerId && journalCustomerId !== activeCustomerId) {
                const clinicName = (userStore.currentCustomers || []).find(c => c.id === journalCustomerId)?.name || '';
                const message = clinicName ? `${switchClinicConfirm}\n\n${clinicName}` : `${switchClinicConfirm}`;
                if (!((await appConfirm(message)) && dialogAction.isCurrent())) {
                    return;
                }
                // Clear cached data, then switch the active clinic (same flow as the clinic
                // switcher in the menu) before navigating to the sale.
                saleStore?.clearAll();
                journalStore?.clearAll();
                visitorStore?.clearAll();
                animalStore?.clearAll();
                calendarStore?.clearAll();
                await userStore.save({ customer: journalCustomerId });
                await userStore.getInfo({ forceLoad: true });
            }

            // TICKET-01653: the journal view is rendered in the STANDARD drawer stack
            // (opened with appState.openDrawer from the journal lists), possibly over a
            // right drawer such as "Journaler". Close both stacks — only level 1 cascades
            // all levels closed and clears the noscroll lock. Closing just the right stack
            // left the journal on top of the sale, and did nothing at all when the user
            // was already on that sale (route() to the current URL is a no-op).
            appState.toggleDrawer(false, 1);
            appState.toggleDrawerRight(false, 1);
            route(`/dyrejournal-sale/${saleId}`);
        } finally {
            dialogAction.finish();
        }
    }

    loadAll = async (props = this.props, emptyFirst = true) => {
        const { id } = props;
        const { journalStore, userStore } = this.props.stores;
        await this.loadJournal(id);
        await journalStore.loadVeterinaries();
        await journalStore.loadPartnerLaunchers(userStore.customer ? userStore.customer.id : 0);
    }

    loadJournal = async id => {
        const { journalStore, animalStore, visitorStore, appState } = this.props.stores;
        // Empty all stores first
        journalStore.updateKeyValue('journal', {});
        // journalStore.updateKeyValue('journals', []);
        animalStore.updateKeyValue('animal', {});
        animalStore.updateKeyValue('animals', []);
        visitorStore.updateKeyValue('visitor', {});
        visitorStore.updateKeyValue('visitors', []);

        await journalStore.load(id, false, { addData: ['animals', 'owners'] });
        const { journal } = journalStore;
        if (journal) {
            // Track journal view
            appState.trackJournal('view', journal);

            const { animal } = journal;
            await animalStore.load(animal);
            const { animal: currentAnimal } = animalStore;
            if (currentAnimal) {
                const { owner } = currentAnimal;
                await visitorStore.load(owner);
            }
        }
    }

    loadJournals = async (animalIds, idNotIn) => {
        const { journalStore, saleStore } = this.props.stores;
        const response = await journalStore.load({
            query: {
                animal: animalIds,
                idNotIn,
            },
            skipUpdate: true,
        });
        journalStore.updateKeyValue('journals', response);
    }

    onImageClick = e => {
        e.stopPropagation();
        e.preventDefault();
        const { image } = e.target.dataset;
        const {
            drawerLevel = 1,
        } = this.props;
        const { appState } = this.props.stores;

        appState.openDrawerRight('viewImage', {
            image,
            callback: async () => {
                // console.log('onClickEditJournal callback');
            },
        }, drawerLevel + 1);
    }

    onClickViewVisit = async e => {
        const { id } = e.target.closest('button').dataset;
        const { drawerLevel = 1 } = this.props;
        const { appState, saleStore } = this.props.stores;
        saleStore.createNewSale({});

        const newSale = await saleStore.load(parseInt(id, 10), false, { skipUpdate: true });
        if (id) {
            await this.loadSalePayments(parseInt(id, 10));
        }
        if (newSale[0].visitors) {
            const visitorIds = newSale[0].visitors.map(e => e.id);
            // console.log('onClickViewVisit', visitorIds)
            await this.loadAnimals(visitorIds);
            await this.loadSales(visitorIds);
        }

        if (newSale[0].animals) {
            const journalIds = newSale[0].journals.map(e => e.id);
            const animalIds = newSale[0].animals.map(e => e.id);
            await this.loadJournals(animalIds, newSale[0].id, journalIds);
        }
        saleStore.updateKeyValue('newSale', newSale[0]);

        appState.appContainerScrollTop();
        appState.toggleDrawerRight();
        appState.toggleDrawer();
    }

    onClickViewVisitor = () => {
        const { appState, saleStore, animalStore, visitorStore } = this.props.stores;
        const { visitor } = visitorStore;
        const visitors = [visitor];
        const { newSale } = saleStore;
        saleStore.createNewSale({});
        saleStore.updateObjectKeyValue('newSale', 'visitors', visitors);
        saleStore.updateObjectKeyValue('newSale', 'id', null);
        saleStore.updateObjectKeyValue('newSale', 'saleNumber', null);
        saleStore.updateObjectKeyValue('newSale', 'saleDate', null);
        saleStore.updateObjectKeyValue('newSale', 'animals', []);

        const visitorIds = visitors.map(v => v?.id);
        if (visitorIds.length > 0) {
            this.loadAnimals(visitorIds);
            this.loadSales(visitorIds);
        }
        animalStore.updateObjectKeyValue('newAnimal', 'owner', visitorIds[0]);

        const { isProductionAnimal } = newSale;
        const isProductionAnimals = util.isDefined(isProductionAnimal) ? isProductionAnimal : visitors.some(v => v?.producerNumber);
        saleStore.updateKeyValue('isProductionAnimals', isProductionAnimals);

        appState.appContainerScrollTop();
        appState.toggleDrawerRight();
        appState.toggleDrawer();
    }

    onClickPrintJournal = () => this.printJournal();

    // TICKET-01660: owner-facing «Hjemsendelsesråd» sheet without the clinical notes.
    onClickPrintClientAdvice = () => this.printJournal({ adviceOnly: true });

    printJournal = ({ adviceOnly = false } = {}) => {
        const { userStore, journalStore } = this.props.stores;
        const { user } = userStore;
        const { language = 'no' } = user;
        const languageDef = countryMap[language || 'default'];
        const { journal } = journalStore;

        // Render Receipt component with data
        const receipt = <IntlProvider definition={languageDef}>
            <DyrejournalJournalPrint stores={this.props.stores} journals={[journal]} adviceOnly={adviceOnly} />
        </IntlProvider>;

        // Append to document temporarily to render
        const tempDiv = document.createElement('div');
        tempDiv.className = 'receipt';
        document.body.appendChild(tempDiv);
        render(receipt, tempDiv);
        const htmlContent = tempDiv.outerHTML;
        document.body.removeChild(tempDiv);
        render(null, tempDiv);
        printViaIframe(htmlContent);
    }

    // onClickViewAnimal = async e => {
    //     const { id } = e.target.closest('i').dataset;
    //     const { animalStore } = this.props.stores;
    //     await animalStore.load(id);
    //     const { animal } = animalStore;



    loadAnimals = async visitorIds => {
        const { animalStore, saleStore, journalStore } = this.props.stores;
        const response = await animalStore.load({
            query: {
                owner: visitorIds,
            },
            skipUpdate: true,
        });
        saleStore.updateKeyValue('visitorAnimals', response);
        journalStore.updateKeyValue('animals', response);
    }

    loadSales = async visitorIds => {
        const { saleStore, userStore } = this.props.stores;
        await saleStore.loadVisitorSales(visitorIds, {
            query: { today: 1 },
            myCustomers: sharedSalesEnabled(userStore),
        });
    }

    loadSalePayments = async saleId => {
        if (!util.isNumber(saleId)) {
            return false;
        }
        const { salePaymentStore, saleStore } = this.props.stores;
        const response = await salePaymentStore.load({
            query: {
                sale: saleId,
            },
            skipUpdate: true,
            limit: 5000,
        });
        saleStore.updateKeyValue('currentPayments', response);
    }

    componentDidMount() {
        this.loadAll();
    }

    render() {
        const {
            week,
            isNew,
            hideAdmin = true,
            drawerLevel = 1,
            showJournals = true,
            animals = [],
        } = this.props;
        const {
            sections,
            sectionsMedical,
            sectionsClinical,
            sectionsPassport,
            sectionsExtra,
            sectionsMedia,
        } = this.state;
        const { userStore, journalStore, animalStore, visitorStore } = this.props.stores;
        const { user, isAdmin, isCustomerAdmin } = userStore;
        const darkmode= util.getNestedValue(user, 'settings.darkmode');

        const { journal, journals, veterinaries, animals: journalAnimals } = journalStore;
        const { animal: currentAnimal } = animalStore;
        // This drawer is opened with only { hideAdmin, height, id } from
        // listJournalMini.js, journals.js and listVisitorJournalsMini.js, so the
        // `animals` prop is always empty. Fall back to the animal loadJournal()
        // already loaded by id — the same fallback newJournal.js uses. Without it
        // `animal` stayed {} and the tannkart (DentalChartSection) species-gated
        // itself away, so it was never visible in the journal view (TICKET-01400).
        const animal = animals.find(a => a.id === journal.animal)
            || journalAnimals.find(a => a.id === journal.animal)
            || currentAnimal
            || {};

        const { visitor } = visitorStore;

        const finalAnimals = animals.length > 0 ? animals : [currentAnimal];

        const { prescriptions } = journal;

        const veterinary = veterinaries.find(vet => vet.id === journal.veterinary);
        const veterinaryName = veterinary ? `${veterinary.firstname} ${veterinary.lastname}` : journal.veterinaryName;

        const weightViewMode = util.getNestedValue(user, 'settings.weightViewMode') || 'graph';

        const { customer } = userStore;
        const { journalItems = {} } = customer;
        const hideFields = Object.keys(journalItems).filter(key => journalItems[key] === 1).map(key => ({ name: key }));
        const hasAudioTranscribe = parseInt(customer.featureFlags?.audioTranscribe, 10) === 1;
        const gateAudioTranscribe = (secs) => secs.map(s => ({
            ...s,
            fields: s.fields.map(f => f.audioTranscribe ? { ...f, audioTranscribe: hasAudioTranscribe } : f),
        }));

        return (
            <div class='w-100 d-flex justify-content-center'>
                <div class='w-100 h-100' style='max-width: 960px; padding-top: 20px; padding-bottom: 100px;'>

                    <Versions stores={this.props.stores} type='journal' id={journal.id} key={`journal-${journal.id}`} />

                    <div class='font-weight-lighter px-3 box-header d-flex flex-row justify-content-between align-items-center'>
                        <span>
                            <Text id='dyrejournal.journal.view'>View Journal</Text>: {journal.id}
                        </span>
                        {journal.sale > 0 && (
                            <button
                                type='button'
                                class='btn btn-sm btn-success rounded-pill ms-2'
                                onClick={this.onClickGotoSale}
                            >
                                <i class='fa-duotone fa-right-to-bracket me-1' />
                                <Text id='dyrejournal.journal.to-sale'>To sale</Text>
                                {journal.saleNumber > 0 && <> {journal.saleNumber}</>}
                            </button>
                        )}
                    </div>

                    {/* <DyrejournalAnimalListMini
                        classNames='mx-4'
                        stores={this.props.stores}
                        animals={[animal]}
                        journals={journals}
                        saleIsLocked={true}
                        // saveSale={saveSale}
                        drawerLevel={drawerLevel}
                        showNewJournal={false}
                        showNewBooking={false}
                        showJournals={showJournals}
                    /> */}
                    <div class='font-weight-lighter text-muted mx-4 mt-5'>
                        <small class='text-uppercase ms-3'>
                            <Text id='dyrejournal.journal.animal'>Animal</Text>
                        </small>
                    </div>
                    <div class={`d-flex flex-column mx-3 px-4 py-3 box-container ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                        {finalAnimals.map(e => {
                            return (<>
                                <div class={`d-flex flex-row justify-content-between px-3 py-0 animal-line`}>
                                    <div class='flex-fill'>
                                        {/* <i class='fa-duotone fa-eye text-muted me-2' data-id={e.id} onClick={this.onClickViewAnimal} /> */}
                                        <span class='font-weight-lighter'>{e.id}:</span> {e.name} ({e.species}/{e.breed})
                                    </div>
                                    <div class='ms-2'>
                                        {e.birth && animalAge(e.birth)}
                                    </div>
                                </div>
                                {/* TICKET-01448: same as the journal-edit drawer (newJournal.js) —
                                    the animal's "Viktig informasjon" is repeated here so it is
                                    visible while reading the journal. Never added to
                                    journalPrint.js or the journal PDF/email sent to the owner. */}
                                {e.importantInfo && <>
                                    <div class={`d-flex flex-row justify-content-between px-3 py-0 mt-2`}>
                                        <div class='flex-fill p-2 rounded bg-info text-white'>
                                            <i class='fa-solid fa-info-circle me-2' />
                                            <strong><Text id='dyrejournal.important-info'>Important info</Text>:</strong> {e.importantInfo}
                                        </div>
                                    </div>
                                </>}
                                {e.insuranceId && <>
                                    <div class={`d-flex flex-row justify-content-between px-3 py-0 animal-line`}>
                                        <div class='flex-fill'>
                                            {e.insuranceId} {e.insuranceCompany ? `(${e.insuranceCompany})` : ''}
                                        </div>
                                    </div>
                                </>}
                                {(e.chipId || journal.animalChipId) && <div class='d-flex flex-row px-3 py-0 animal-line'>
                                    <div class='flex-fill'>
                                        <i class='fa-regular fa-microchip me-2' />
                                        <Text id='input.chipId'>Chip ID</Text>: {e.chipId || journal.animalChipId}
                                    </div>
                                </div>}
                            </>);
                        })}
                    </div>

                    {finalAnimals.map(e => (
                        e.allWeights && e.allWeights.length > 2 && <div class={`mx-3 px-4 pb-3 box-container ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                            <div class='d-flex flex-row justify-content-end px-1 py-0 mt-1'>
                                <button
                                    class='btn btn-sm btn-link text-body-secondary py-0 px-1'
                                    onClick={this.toggleWeightView}
                                    title={weightViewMode === 'graph' ? 'Show table' : 'Show graph'}
                                >
                                    <i class={`fa-solid ${weightViewMode === 'graph' ? 'fa-table' : 'fa-chart-line'}`} />
                                </button>
                            </div>
                            {weightViewMode === 'graph' ? (
                                <div class='d-flex flex-row justify-content-end px-1 py-0'>
                                <LineGraphMini
                                    stores={this.props.stores}
                                    dataLeft={[e.allWeights.sort(sortByDateAsc).map(d => ({
                                        epoch: Math.floor(new Date(d.date).getTime()),
                                        x: Math.floor(new Date(d.date).getTime()),
                                        y: d.weight
                                    }))]}
                                    legendLeft={['Vekt']}
                                    unitLeft={['kg']}
                                    yTicksLeftVal={val => `${val} kg`}
                                    xTicksVal={val => new Date(val).toLocaleString('nb-NO', {
                                        day: '2-digit',
                                        month: 'short',
                                        year: '2-digit',
                                    })}
                                    width={e.allWeights.length > 10 ? 600 : 300}
                                    height={50}
                                    smoothLeft={[true]}
                                    gradientFillLeft={['#0d9488']}
                                    gradientOpacity={0.15}
                                    inputStyleLeft={'stroke: #0d9488 !important; stroke-width: 2px !important;'}
                                    showValueLeftLine={true}
                                />
                                </div>
                            ) : (
                                <div class='px-1' style='line-height: 1.2; max-height: 100px; overflow-y: auto;'>
                                    <table class='table table-sm mb-0' style='font-size: 0.75rem;'>
                                        <thead>
                                            <tr>
                                                <th class='fw-normal text-body-secondary py-0'><Text id='dyrejournal.weight-date'>Date</Text></th>
                                                <th class='fw-normal text-body-secondary text-end py-0'><Text id='dyrejournal.weight-kg'>Weight (kg)</Text></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {[...e.allWeights].sort(sortByDateAsc).reverse().map(w => (
                                                <tr>
                                                    <td class='py-0'>{new Date(w.date).toLocaleString('nb-NO', {
                                                        day: '2-digit',
                                                        month: 'short',
                                                        year: '2-digit',
                                                    })}</td>
                                                    <td class='text-end py-0'>{w.weight} kg</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    ))}

                    {visitor && visitor.id && <>
                        <div class='font-weight-lighter text-muted mx-4 mt-3'>
                            <small class='text-uppercase ms-3'>
                                <Text id='dyrejournal.journal.owner'>Owner</Text>
                            </small>
                        </div>
                        <div class={`d-flex flex-column mx-3 px-4 py-3 box-container ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>

                            <div class={`d-flex flex-row justify-content-between px-3 py-0 animal-line`}>
                                <div class='flex-fill'>
                                    <i class='fa-duotone fa-eye text-muted me-2' onClick={this.onClickViewVisitor} />
                                    <span class='font-weight-lighter'>{visitor.id}:</span> {visitor.firstname ? `${visitor.firstname} ${visitor.lastname}` : `${visitor.name}`}
                                </div>
                                <div class='ms-2'>
                                    {visitor.cellphone}
                                </div>
                            </div>
                        </div>
                    </>}

                    {journal.sale > 0 && <>
                        <div class='font-weight-lighter text-muted mx-4 mt-4'>
                            <small class='text-uppercase ms-3'>
                                <Text id='dyrejournal.journal.sale'>Sale</Text>
                            </small>
                        </div>
                        <div class={`d-flex flex-column mx-3 px-4 py-3 box-container ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                            <div class={`d-flex flex-row justify-content-between px-3 py-0 animal-line`}>
                                <button
                                    class='btn btn-sm btn-outline-success rounded-pill'
                                    data-id={journal.sale}
                                    onClick={this.onClickViewVisit}
                                >
                                    <Text id='dyrejournal.goto-sale'>Go to sale</Text>: {journal.sale}
                                </button>
                            </div>
                        </div>
                    </>}

                    {journal.auditTrail?.length > 0 && <>
                        <div class='font-weight-lighter text-muted mx-4 mt-4'>
                            <small class='text-uppercase ms-3'>
                                <Text id='dyrejournal.journal.audit-trail'>Journal history</Text>
                            </small>
                        </div>
                        <div class={`d-flex flex-column mx-3 px-4 py-3 box-container ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                            {journal.auditTrail.map((entry, index) => (
                                <div key={index} class='d-flex flex-row justify-content-between px-3 py-1'>
                                    <div>
                                        {entry.action === 'reopen' ? <>
                                            <i class='fa-duotone fa-unlock me-2' />
                                            <Text id='dyrejournal.journal.audit-reopened'>Reopened</Text>
                                        </> : entry.action === 'delete' ? <>
                                            <i class='fa-duotone fa-trash me-2' />
                                            <Text id='dyrejournal.journal.audit-deleted'>Deleted</Text>
                                        </> : entry.action === 'undelete' ? <>
                                            <i class='fa-duotone fa-trash-slash me-2' />
                                            <Text id='dyrejournal.journal.audit-undeleted'>Restored</Text>
                                        </> : <>
                                            <i class='fa-duotone fa-lock me-2' />
                                            <Text id='dyrejournal.journal.audit-closed'>Closed</Text>
                                        </>}
                                        {entry.userName ? <span class='ms-2'>{entry.userName}</span> : ''}
                                        {entry.reason ? <div class='text-body-secondary ms-4 font-italic'>{entry.reason}</div> : ''}
                                    </div>
                                    <div class='text-body-secondary'>
                                        <nobr>{util.formatDate(entry.date, { hour12: false, hour: '2-digit', minute: '2-digit', locale: 'nb-NO' })}</nobr>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>}

                    <DyrejournalLinkJournalToSale
                        stores={this.props.stores}
                        journal={journal}
                        animal={currentAnimal}
                        startExpanded={true}
                        onLinked={() => this.loadJournal(journal.id)}
                    />

                    <div class='bg-light d-flex flex-column justify-content-start h-100 pt-3'>
                        <ViewFields
                            stores={this.props.stores}
                            sections={sections}
                            fields={['date', 'id', 'weight', 'comment']}
                            holdingStore={journalStore}
                            objectName={isNew ? 'newJournal' : 'journal'}
                            isNew={isNew}
                            hideAdmin={hideAdmin}
                            drawerLevel={drawerLevel}
                            hideFields={hideFields}

                            // filter={field => {
                            // }}
                        >

                            <div
                                class={`d-flex flex-row align-items-center position-relative ps-1 pe-3 w-100`}
                            >
                                <div
                                    class={`d-flex justify-content-center align-items-center text-muted`}
                                    style={`
                                        width: 44px;
                                        height: 44px;
                                        min-width: 44px;
                                        opacity: 0.3;
                                    `}
                                >
                                    <i class={`fa-solid fa-user-doctor`} />
                                </div>
                                <span class={`flex-grow-1 overflow-auto`}>
                                    <div class='p-3 position-relative'>
                                        <small class='text-muted position-absolute' style='top: 0px; left: 0px;'>
                                            <Text id='input.veterinary'>Veterinary</Text>
                                        </small>
                                        {veterinaryName}
                                    </div>
                                </span>
                            </div>


                        </ViewFields>


                    </div>

                    <div class='font-weight-lighter text-muted mx-4'>
                        <small class='text-uppercase ms-3'>
                            <Text id='dyrejournal.journal.prescriptions'>Journal prescriptions</Text>
                        </small>
                    </div>
                    <DyrejournalPrescriptionListMini
                        classNames='mx-4'
                        stores={this.props.stores}
                        prescriptions={prescriptions}
                        saleIsLocked={true}
                        saveJournal={this.saveJournal}
                        drawerLevel={drawerLevel}
                        journal={journal.id}
                    />

                    {/* Read view: banner derived from THIS journal; the editor owns the shared card state. */}
                    <PartnerSuggestionsBanner
                        stores={this.props.stores}
                        journal={journal}
                        pending={pendingSectionsFromJournal(journal).pending}
                    />
                    <PartnerLaunchButtons stores={this.props.stores} journal={journal} />

                    {customer.featureFlags?.idexx === 1 && <>
                        <div class='font-weight-lighter text-muted mx-4 mt-4'>
                            <small class='text-uppercase ms-3'>
                                Lab results
                            </small>
                        </div>
                        <DyrejournalLabResultsListMini
                            classNames='mx-4'
                            stores={this.props.stores}
                            animal={journal.animal}
                            journal={journal}
                            saleIsLocked={true}
                            drawerLevel={drawerLevel}
                        />
                    </>}

                    <DentalChartSection
                        stores={this.props.stores}
                        journal={journal}
                        animal={animal}
                        objectName={isNew ? 'newJournal' : 'journal'}
                        readOnly={true}
                    />

                    {journal.description && <>
                        <div class='font-weight-lighter text-muted mx-4 mt-4'>
                            <small class='text-uppercase ms-3'>
                                <Text id='dyrejournal.journal.imported-description'>Journal text from previous system</Text>
                            </small>
                        </div>
                        <div class={`d-flex flex-column mx-3 px-4 py-3 box-container ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                            <div class='px-3'>
                                <Markdown markdown={journal.description || ''} markedOpts={localUtil.getMarkdownOptions()} />
                            </div>
                        </div>
                    </>}

                    <div class='bg-light d-flex flex-column justify-content-start mt-4'>
                        <ViewFields
                            stores={this.props.stores}
                            sections={sectionsClinical}
                            holdingStore={journalStore}
                            objectName={isNew ? 'newJournal' : 'journal'}
                            isNew={false}
                            hideAdmin={hideAdmin}
                            drawerLevel={drawerLevel}
                            hideFields={hideFields}

                            // filter={field => {
                            // }}
                        />
                    </div>

                    <div class='bg-light d-flex flex-column justify-content-start mt-4'>
                        <ViewFields
                            stores={this.props.stores}
                            sections={sectionsMedical}
                            holdingStore={journalStore}
                            objectName={isNew ? 'newJournal' : 'journal'}
                            isNew={false}
                            hideAdmin={hideAdmin}
                            drawerLevel={drawerLevel}
                            hideFields={hideFields}

                            // filter={field => {
                            // }}
                        />
                    </div>

                    <div class='bg-light d-flex flex-column justify-content-start mt-4'>
                        <ViewFields
                            stores={this.props.stores}
                            sections={sectionsPassport}
                            holdingStore={journalStore}
                            objectName={isNew ? 'newJournal' : 'journal'}
                            isNew={false}
                            hideAdmin={hideAdmin}
                            drawerLevel={drawerLevel}
                            hideFields={hideFields}

                            // filter={field => {
                            // }}
                        />
                    </div>

                    <div class='bg-light d-flex flex-column justify-content-start mt-4'>
                        <ViewFields
                            stores={this.props.stores}
                            sections={sectionsExtra}
                            holdingStore={journalStore}
                            objectName={isNew ? 'newJournal' : 'journal'}
                            isNew={false}
                            hideAdmin={hideAdmin}
                            drawerLevel={drawerLevel}
                        />
                    </div>

                    {journal.images && journal.images.length > 0 && <>
                        <div class='w-100 d-flex flex-column'>
                            <div class='px-0 box-header-info d-flex flex-column'>
                                <div class='d-flex'>
                                    {/* {JSON.stringify(part)} */}
                                    <ImageScroller
                                        images={journal.images}
                                        stores={this.props.stores}
                                        maxHeight={'60vh'}
                                        showImg={img => localUtil.displayImg(img, this.props, '1920x')}
                                        onImageClick={this.onImageClick}
                                    />
                                </div>
                            </div>
                        </div>
                    </>}

                    <div class='bg-light d-flex flex-column justify-content-start mt-4'>
                        <ViewFields
                            stores={this.props.stores}
                            sections={sectionsMedia}
                            holdingStore={journalStore}
                            objectName={isNew ? 'newJournal' : 'journal'}
                            isNew={false}
                            hideAdmin={hideAdmin}
                            drawerLevel={drawerLevel}
                            hideFields={hideFields}

                            // filter={field => {
                            // }}
                        />
                    </div>


                    <div class='bg-light d-flex flex-column justify-content-start mt-4'>
                        <FormFields
                            stores={this.props.stores}
                            sections={gateAudioTranscribe(sections)}
                            fields={['comment']}
                            holdingStore={journalStore}
                            // objectName={isNew ? 'newJournal' : 'journal'}
                            objectName={isNew ? 'newJournal' : 'journal'}
                            isNew={journal.id ? false : true}
                            hideAdmin={hideAdmin}
                            drawerLevel={drawerLevel}
                            hideFields={hideFields}

                            // filter={field => {
                            // }}
                        />
                    </div>
                    <div class='bg-light d-flex flex-column justify-content-start mt-4'>
                        <FormFields
                            stores={this.props.stores}
                            sections={gateAudioTranscribe(sectionsMedia)}
                            fields={['files', 'images', 'links', 'emails']}
                            holdingStore={journalStore}
                            // objectName={isNew ? 'newJournal' : 'journal'}
                            objectName={isNew ? 'newJournal' : 'journal'}
                            isNew={journal.id ? false : true}
                            hideAdmin={hideAdmin}
                            drawerLevel={drawerLevel}
                            hideFields={hideFields}

                            // filter={field => {
                            // }}
                        />
                    </div>

                    {journal.id > 0 && isCustomerAdmin
                        && parseInt(customer.featureFlags?.journalFeedback, 10) === 1 && (
                        <JournalFeedback
                            stores={this.props.stores}
                            journal={journal}
                        />
                    )}

                    <div class='bg-light d-flex flex-column justify-content-start mt-4'>
                        <button
                            class={`btn btn-info ms-2 rounded-pill`}
                            onClick={this.onClickPrintJournal}
                        >
                            <i class='fa-solid fa-print' /> <Text id='dyrejournal.print-journal'>Print journal</Text>
                        </button>
                        <button
                            type='button'
                            class={`btn btn-outline-info ms-2 mt-2 rounded-pill`}
                            onClick={this.onClickPrintClientAdvice}
                            disabled={!hasClientAdvice(journal)}
                        >
                            <i class='fa-solid fa-print' /> <Text id='dyrejournal.print-client-advice'>Print home care advice</Text>
                        </button>
                    </div>

                </div>
            </div>
        );
    }
}

export default ViewJournal;
